import { Injectable } from '@angular/core'
import {
  Observable,
  combineLatest,
  concatMap,
  from,
  last,
  map,
  of,
  scan,
  switchMap,
} from 'rxjs'
import { Page } from '../../models/util.model'
import { Carrier, CarriersService } from '../carriers'
import { Payment, PaymentsService } from '../payments'
import {
  NgModalServiceInterface,
  ModalResponse,
} from '../../models/modal.model'
import { NgNotificationServiceInterface } from '../../models/notification.model'
import { InvoicesService } from './invoices.service'
import { CurrenciesService, Currency } from '../currencies'
import { InvoiceSearchParams } from './models/invoice-search.model'
import {
  InvoicesListingData,
  InvoicesListingPage,
} from './models/invoice-listing.model'
import { Invoice } from './models/invoice.entity'
import { parseInvoicePageKeys } from './libs/invoices-page.lib'
import {
  InvoiceModalCode,
  InvoiceNotificationCode,
  parseInvoiceModal,
  parseInvoiceNotification,
} from './libs/invoice-notification.lib'
import { Channel, ChannelsService } from '../channels'
import { uniqBy } from 'lodash'

@Injectable({
  providedIn: 'root',
})
export class InvoicesRepository {
  constructor(
    private invoicesService: InvoicesService,
    private carriersService: CarriersService,
    private channelsService: ChannelsService,
    private currenciesService: CurrenciesService,
    private paymentsService: PaymentsService,
    private ngModalService?: NgModalServiceInterface,
    private ngNotificationService?: NgNotificationServiceInterface,
  ) {}

  /**
   * Search goods-returns and external data
   * @param searchParams - the search params
   * @param fields - the fields
   * @param listingData - the already loaded listing data
   * @returns the observable for search goods-returns
   */
  searchInvoices$(
    searchParams: InvoiceSearchParams,
    listingData?: InvoicesListingData,
    returnAll?: boolean,
  ): Observable<InvoicesListingPage> {
    const needsPagination =
      searchParams.limit !== undefined &&
      searchParams.limit > this.invoicesService.limit

    return needsPagination
      ? this._paginateInvoices$(searchParams, listingData)
      : this._searchInvoices$(searchParams, listingData, returnAll)
  }

  /**
   * Search goods returns with relative external data
   * @param searchParams - the search params
   * @param fields - the goods-returns fields
   * @param listingData - the already loaded listing data
   * @returns the observable<InvoicesListingPage> for search goods-returns
   */
  private _searchInvoices$(
    searchParams: InvoiceSearchParams,
    listingData?: InvoicesListingData,
    returnAll?: boolean,
  ): Observable<InvoicesListingPage> {
    return this.invoicesService
      .search$(searchParams, returnAll)
      .pipe(
        switchMap((page) =>
          this._loadExtData$(page, listingData).pipe(
            map((extData) => ({ ...page, extData })),
          ),
        ),
      )
  }

  /**
   * Load external page goods-returns data
   * @param page - the goods-returns page
   * @param fields - the goods-returns fields
   * @returns the observable for load external data
   */
  private _loadExtData$(
    page: Page<Invoice>,
    listingData?: InvoicesListingData,
  ): Observable<InvoicesListingData> {
    const extDataKeys = parseInvoicePageKeys(page)
    const obs$: Partial<{
      carriers: Observable<Carrier[]>
      channels: Observable<Channel[]>
      currencies: Observable<Currency[]>
      payments: Observable<Payment[]>
    }> = {}

    // Only load carriers if we don't already have them or if we need new ones
    if (extDataKeys.carrierIds) {
      const existingCarriers = listingData?.carriers || []
      const hasAllCarriers = extDataKeys.carrierIds.every((id) =>
        existingCarriers.some((carrier) => carrier._id === id),
      )

      if (!hasAllCarriers) {
        obs$.carriers = this.carriersService.store$(
          extDataKeys.carrierIds,
          existingCarriers,
        )
      } else {
        obs$.carriers = of(existingCarriers)
      }
    }

    if (extDataKeys.channelIds) {
      const existingChannels = listingData?.channels || []
      const hasAllChannels = extDataKeys.channelIds.every((id) =>
        existingChannels.some((channel) => channel._id === id),
      )

      if (!hasAllChannels) {
        obs$.channels = this.channelsService.store$(
          extDataKeys.channelIds,
          existingChannels,
        )
      } else {
        obs$.channels = of(existingChannels)
      }
    }

    const existingCurrencies = listingData?.currencies || []
    obs$.currencies = existingCurrencies.length
      ? of(existingCurrencies)
      : this.currenciesService.list$()

    const existingPayments = listingData?.payments || []
    obs$.payments = existingPayments.length
      ? of(existingPayments)
      : this.paymentsService.list$()

    return combineLatest(obs$).pipe(
      map((results) => {
        const extData: InvoicesListingData = {}
        if (results.carriers) extData.carriers = results.carriers
        if (results.channels) extData.channels = results.channels
        if (results.currencies) extData.currencies = results.currencies
        if (results.payments) extData.payments = results.payments
        return extData
      }),
    )
  }

  /**
   * Search more than 200 goods-returns with relative external data
   * @param searchParams - the search params
   * @param fields - the goods-returns fields
   * @param listingData - the already loaded listing data
   * @returns the observable<InvoicesListingPage> for search goods-returns
   */
  private _paginateInvoices$(
    searchParams: InvoiceSearchParams,
    listingData?: InvoicesListingData,
  ): Observable<InvoicesListingPage> {
    const totalCount = searchParams.limit || this.invoicesService.limit
    const pagesCount = Math.ceil(totalCount / this.invoicesService.limit)

    return from(
      Array.from({ length: pagesCount }, (_, i) => ({
        ...searchParams,
        limit: this.invoicesService.limit,
        offset: i * this.invoicesService.limit,
      })),
    ).pipe(
      concatMap((pageParams) => this._searchInvoices$(pageParams, listingData)),
      scan<InvoicesListingPage, InvoicesListingPage>(
        (acc, current) => ({
          totalCount: uniqBy(
            [...(acc.data || []), ...(current.data || [])],
            '_id',
          ).length,
          data: uniqBy([...(acc.data || []), ...(current.data || [])], '_id'),
          extData: { ...acc.extData, ...current.extData },
        }),
        { totalCount: 0, data: [], extData: {} } as InvoicesListingPage,
      ),
      last(),
    )
  }

  notify(options: { code?: InvoiceNotificationCode; message?: string }): void {
    this.ngNotificationService?.show(
      options.message
        ? { title: 'Error', message: options.message, color: 'error' }
        : parseInvoiceNotification(options.code),
    )
  }

  async alert(
    code: InvoiceModalCode,
    invoice?: Invoice,
  ): Promise<ModalResponse> {
    const settings = parseInvoiceModal(code, invoice)
    if (!this.ngModalService || !settings) {
      return 'closed'
    }
    return await this.ngModalService.showDialog(settings)
  }
}
